import "./CloneProgress.scss";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import ProgressIdle from "@/components/CloneProgress/components/ProgressIdle";
import {
  getBulkCloneCount,
  getDarkMode,
  setBulkCloneCount,
} from "@/components/CloneProgress/state/cloneProgressReducer";
import type { CloneReportProps } from "@/components/CloneReport/CloneReport";
import CloneReport from "@/components/CloneReport/CloneReport";
import type { CloneOptions } from "@/components/CloneView/components/CloneOptions/CloneOptionsSection";
import { clonePlusDialog } from "@/lib/monday";
import { prepareBatchItemActions } from "@/services/automation-helper";
import type { RunResult } from "@/services/runcli-service";
import { servicesConfig } from "@/services/services-configs";
import type { AutomationActions } from "@/types/automation";
import type { PollResult, ResultType } from "@/types/common";
import { ExitCode } from "@/types/common";

import { useCurrentMondayContext } from "../AppContext/WithMondayContextAvailable";

interface PollArgs {
  requestId: string;
}

export function CloneProgress() {
  const queryClient = useQueryClient();
  const mondayContext = useCurrentMondayContext();
  const { mondayService, cliService } = servicesConfig();
  const [automationActions, setAutomationActions] = useState<AutomationActions[]>([]);
  const [step, setStep] = useState<number>(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [completedPercentage, setCompletedPercentage] = useState(0);
  const isDark = useSelector(getDarkMode);
  const [numberOfSelectedItems, setNumberOfSelectedItems] = useState(0);
  const dispatch = useDispatch();
  const bulkCloneCount = useSelector(getBulkCloneCount);

  const move = () => {
    setTimeout(() => {
      setStep(step + 1);
    }, 7000);
  };
  useEffect(() => {
    const usageData = clonePlusDialog.getUsageDataFromParent();
    if (usageData) {
      const { selectedItems, selectedTargetBoard, cloneOptions, postfix } = JSON.parse(usageData.dataJson) as {
        selectedItems: string[];
        selectedTargetBoard: string;
        cloneOptions: CloneOptions;
        postfix: string;
      };
      setNumberOfSelectedItems(selectedItems.length);
      const automationActions = prepareBatchItemActions(selectedItems, selectedTargetBoard, cloneOptions, postfix);
      setAutomationActions(automationActions);
    }
  }, []);
  useEffect(() => {
    setCompletedPercentage(Math.round((100 / numberOfSelectedItems) * bulkCloneCount));
  }, [numberOfSelectedItems, bulkCloneCount]);

  const {
    data: requestId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isFetching: isFetchingCommandId,
    //?? refetch: doRunCommand,
    error: runError,
  } = useQuery<RunResult, Error>({
    enabled: automationActions.length > 0,
    retry: false,
    queryKey: ["runCommand", { automationActions: automationActions }],
    queryFn: async () => {
      const requestId = crypto.randomUUID();
      const sessionToken = await mondayService.getSessionToken();
      await cliService.runActions(automationActions, requestId, mondayContext, sessionToken);
      return requestId;
    },
  });
  const {
    data: commandResult,
    isFetching: isFetchingCommandResult,
    error: pollError,
  } = useQuery<PollResult | undefined, Error>({
    // We will retry every 'retryDelay' ms at most 'retry' times until result.status is not PENDING
    retry: 600, // retry every two seconds for 20 minutes
    retryDelay: 2000, // two seconds
    enabled: !!requestId,
    queryKey: ["pollCommand", { requestId: requestId }],
    queryFn: async () => {
      if (!requestId) {
        // If this happens, it's a logic error since the query is only enabled when there is a requestId
        console.error("Request ID expected.");
        return;
      }
      const sessionToken = await mondayService.getSessionToken();
      const result: PollResult = await cliService.pollCommand(requestId, mondayContext, sessionToken);
      const { bulkCloneCount } = parseResult(result?.results ?? []);
      dispatch(setBulkCloneCount(bulkCloneCount));
      setTimeElapsed(timeElapsed + 2);
      if (result.retry) {
        throw new Error("Results pending...");
      }
      if (result.duration) {
        setTimeElapsed(result.duration / 1000);
      }
      move();
      return result;
    },
  });

  const prepareResultJson = (): CloneReportProps | undefined => {
    if (isFetchingCommandResult) {
      return;
    }
    if (runError) {
      console.error(runError);
      const message = `Unable to submit command: ${formatErrorMessage(runError)}`;
      return {
        isProcessFailed: true,
        results: [{ status: "failed", label: message }],
        bulkCloneCount: 0,
        noOfTries: 0,
      };
    }
    if (pollError) {
      console.error(pollError);
      const message = `Unable to retrieve results: ${formatErrorMessage(pollError)}`;
      return {
        isProcessFailed: true,
        results: [{ status: "failed", label: message }],
        bulkCloneCount: 0,
        noOfTries: 0,
      };
    }
    if (!commandResult) {
      return;
    }
    if (!commandResult.results || commandResult.results.length === 0) {
      console.error(commandResult);
      const message = `Unexpected results.`;
      return {
        isProcessFailed: true,
        results: [{ status: "failed", label: message }],
        bulkCloneCount: 0,
        noOfTries: 0,
      };
    }

    const { results, bulkCloneCount } = parseResult(commandResult.results);
    return {
      isProcessFailed: commandResult.exitCode !== ExitCode.SUCCESS,
      results: results,
      duration: commandResult.duration,
      bulkCloneCount: bulkCloneCount,
      noOfTries: 0,
    };
  };

  const isItemAddedToBoard = (text: string) => {
    const regex = /Item.*? with id (\d+) added to board.*?with id (\d+)/;
    if (regex.test(text)) {
      return true;
    }
    return false;
  };
  const isActionsSuccessful = (text: string) => {
    const regex = /(\d+) actions were successful/;
    if (regex.test(text)) {
      return true;
    }
    return false;
  };

  const getActionCount = (s: string) => {
    const regex = /(\d+)/gi;
    try {
      const matches: RegExpExecArray | null = regex.exec(s);
      if (matches && matches.length === 2) {
        const actions = parseInt(matches[1], 10);
        if (isNaN(actions)) {
          return 0;
        }
        return actions;
      }
      return 0;
    } catch (e) {
      return 0;
    }
  };

  const parseResult = (
    commandResult: ResultType[]
  ): { results: { status: string; label: string }[]; bulkCloneCount: number } => {
    const results: { status: string; label: string }[] = [];
    let bulkCloneCount = 0;
    let data;
    commandResult.forEach((result: ResultType) => {
      const { exitCode } = result;
      switch (exitCode) {
        case ExitCode.SUCCESS:
          data = result.output.split("\n");
          Object.values(data).forEach((s) => {
            if (isItemAddedToBoard(s)) {
              results.push({ status: `${result.exitCode}`, label: s });
            } else if (isActionsSuccessful(s)) {
              bulkCloneCount += getActionCount(s);
            }
          });
          break;
        default:
          results.push({ status: `${result.exitCode}`, label: result.output });
      }
    });
    return { results, bulkCloneCount };
  };

  useEffect(() => {
    cleanUpQueries();
  }, [requestId]);
  function cleanUpQueries() {
    queryClient.removeQueries({
      predicate: (query) => {
        if (requestId) {
          const args = query.queryKey[1] as PollArgs;
          return query.queryKey[0] === "pollCommand" && args?.requestId !== requestId;
        }
        return false;
      },
    });
  }

  const formatStartTime = () => {
    const date = new Date();
    let hr: number | string = date.getHours();
    let min: number | string = date.getMinutes();

    if (min < 10) {
      min = `0${min}`;
    }
    let ampm = "AM";
    if (hr > 12) {
      hr -= 12;
      ampm = "PM";
    }
    if (hr < 10) {
      hr = `0${hr}`;
    }
    return `${hr}:${min} ${ampm}`;
  };

  if (step === 0) {
    return (
      <div className="appfire-dialog-progress-idle">
        <ProgressIdle isDark={isDark} />
        <span className="in-progress">
          Cloning in progress...
          <br />
        </span>
        <h1 className="progress-percentage">{completedPercentage}% complete </h1>
        <span className="time-elapsed">
          {" "}
          {timeElapsed} seconds elapsed today. <br />
          Started today at {formatStartTime()}.
        </span>
      </div>
    );
  } else if (step === 1) {
    const preparedResult: CloneReportProps | undefined = prepareResultJson();
    if (preparedResult) {
      return (
        <CloneReport
          bulkCloneCount={preparedResult?.bulkCloneCount}
          results={preparedResult?.results}
          isProcessFailed={preparedResult?.isProcessFailed}
          noOfTries={preparedResult.noOfTries}
        />
      );
    } else {
      return <></>;
    }
  } else {
    return <></>;
  }
}
function formatErrorMessage(err: Error): string {
  let message: string;
  if (err.stack) {
    // Lambda errors seem to contain the name & message in the stack value
    message = err.stack;
  } else {
    message = `${err.name}: ${err.message}`;
  }
  return message;
}
